import styled from '@emotion/styled'
import React from 'react'
import { PlusIcon, MinusIcon } from 'shop/assets/icons'
import { StyledHTMLElement } from 'shop/theme/types'

interface Props {
  quantity: number
  onIncrement: () => void
  onDecrement: () => void
  quantityOpacity?: number
  disableDecrement?: boolean
  disableIncrement?: boolean
  customDecrementIcon?: React.ReactNode
  fontSize?: string
  width?: string
}

/** Reusable component for controlling item quantities with increment/decrement buttons */
const QuantityControl = ({
  quantity,
  onIncrement,
  onDecrement,
  disableDecrement = false,
  disableIncrement = false,
  quantityOpacity = 1,
  customDecrementIcon,
  fontSize,
  width
}: Props) => {
  return (
    <Container data-testid='quantity-container'>
      <QuantityButton
        data-testid='quantity-decrement-button'
        onClick={onDecrement}
        disabled={disableDecrement}
      >
        {!!customDecrementIcon ? (
          customDecrementIcon
        ) : (
          <MinusIcon data-testid='quantity-minus-icon' />
        )}
      </QuantityButton>

      <QuantityLabel
        data-testid='quantity'
        quantityOpacity={quantityOpacity}
        width={width}
        fontSize={fontSize}
      >
        {quantity}
      </QuantityLabel>

      <QuantityButton
        data-testid='quantity-increment-button'
        onClick={onIncrement}
        disabled={disableIncrement}
      >
        <PlusIcon />
      </QuantityButton>
    </Container>
  )
}

// Circular button for increment/decrement actions
const QuantityButton = styled.button<StyledHTMLElement>(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  outline: 0,
  backgroundColor: theme.colors['white'],
  color: theme.colors['primary'],
  border: 0,
  cursor: 'pointer',
  padding: 0,
  width: '30px',
  height: '30px',
  borderRadius: '100%',
  WebkitTapHighlightColor: 'transparent',
  '&:disabled': {
    cursor: 'not-allowed',
    opacity: 0.2
  },
  '@media (hover: hover)': {
    '&:hover': {
      backgroundColor: '#F5F5F5'
    }
  },
  '&:active': {
    backgroundColor: '#D9D9D9'
  },
  '& > svg': {
    height: '16px',
    width: '16px',
    '& rect': {
      fill: theme.colors['primary'],
      height: '1.5px'
    }
  }
}))

// Pill-shaped container holding the quantity controls
const Container = styled.div(({ theme }) => ({
  display: 'flex',
  fontWeight: theme.fontWeights.bold,
  backgroundColor: '#F5F5F5',
  padding: '2px',
  borderRadius: '18px'
}))

// Centered quantity display between the buttons
const QuantityLabel = styled.span<
  StyledHTMLElement & {
    quantityOpacity: number
    width?: string
    fontSize?: string
  }
>(({ theme, quantityOpacity, width, fontSize }) => ({
  fontSize: fontSize || theme.fontSizes[2],
  margin: 'auto',
  textAlign: 'center',
  opacity: quantityOpacity,
  width: width || `${theme.fontSizes[3] * 1.5 + theme.space[3] * 1}px`
}))

export default QuantityControl
